import { useState } from 'react';

import isEqual from 'lodash/isEqual';

import { parseApiError } from '@cast/api-client';
import { Button, DialogFooter } from '@cast/design-system';
import { K8sProvider } from '@cast/shared-types';

import { useUpdateClusterMutation } from 'hooks/mutations';
import { useConnectAndEnableCastAiScriptQuery } from 'hooks/queries/cluster/useConnectAndEnableCastAiScriptQuery';

import {
  ManualCredentialsInputFormValue,
  useManualCredentialsInput,
} from './_hooks/useManualCredentialsInput';
import { ManualCredentialsSection } from './ManualCredentialsSection';
import {
  AnimatedOverlay,
  ClusterDialogHeading,
  FeaturesScriptSplitDialogContent,
  IRanTheScriptButton,
  ScriptSection,
  SimpleFeaturesSection,
  StepContainer,
} from '../../../../../_components/new-onboarding';
import { TrialScriptOverlay } from '../../../../../components/TrialScriptOverlay';
import { isPremiumFeatureSelected } from '../../../../../utils';
import { useAssignCredentialsDialogV2Context } from '../../context';

import {
  useCluster,
  isEKSCluster,
} from '@cast/shared-cluster-context-data-access';

import { RffForm } from '@cast/shared-rff-ui';

const securityConstructByProvider: Partial<Record<K8sProvider, string>> = {
  [K8sProvider.EKS]: 'generated role ARN',
  [K8sProvider.AKS]: 'App secret',
  [K8sProvider.GKE]: 'Service Account JSON',
  [K8sProvider.KOPS]: 'IAM user key/secret',
};

type AssignCredentialsSetupSlideProps = {
  isActiveFeaturesLoading: boolean;
};

export const AssignCredentialsSetupSlide = ({
  isActiveFeaturesLoading,
}: AssignCredentialsSetupSlideProps) => {
  const {
    setActiveSlide,
    featureSelection,
    updateFeature,
    alreadyInstalledFeatures,
    dismiss,
    isPreTrial,
  } = useAssignCredentialsDialogV2Context();
  const hasPremiumFeatureSelected = isPremiumFeatureSelected(featureSelection);
  const [isEnteringManually, setIsEnteringManually] = useState(false);

  const [apiError, setApiError] = useState<string | undefined>();
  const { cluster } = useCluster();
  const { isLoading, isFetching, error, refetch, script } =
    useConnectAndEnableCastAiScriptQuery({
      clusterName: cluster.name,
      clusterProvider: cluster.providerType,
      installGPUPlugin: featureSelection.nvidiaGpuPlugin,
      installSecurityAgent: featureSelection.security,
      installSecurityCompliance: false,
      installSecurityImageScanning: false,
      installAutoscalerAgent: featureSelection.autoscaler,
      installNetflowExporter: featureSelection.networkCostMetrics,
      installGpuMetricsExporter: featureSelection.gpuMetricsExporter,
      installReliabilityMetrics: false,
      installWorkloadAutoscaler: featureSelection.workloadAutoscaler,
      installOmni: featureSelection.omni,
      installAiOptimizerProxy: false,
      installPodMutator: hasPremiumFeatureSelected,
    });

  const securityConstruct = securityConstructByProvider[cluster.providerType];
  const { validate, input, initialValue, getValue } =
    useManualCredentialsInput();

  const { mutate, isPending: isSubmitting } = useUpdateClusterMutation(
    cluster.id
  );

  return (
    <StepContainer data-testid="phase-2-onboarding-setup-step">
      <RffForm
        initialValues={initialValue}
        initialValuesEqual={isEqual}
        validate={validate}
        onSubmit={(value: ManualCredentialsInputFormValue) => {
          setApiError(undefined);
          mutate(getValue(value), {
            onSuccess: () => setActiveSlide('success'),
            onError: (error: unknown) => setApiError(parseApiError(error)),
          });
        }}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <ClusterDialogHeading cluster={cluster} onClose={dismiss} />

        <FeaturesScriptSplitDialogContent
          leftTitle="Automation features"
          left={
            <SimpleFeaturesSection
              shouldShowAutoscalerSelection={false}
              featureSelection={featureSelection}
              updateFeature={updateFeature}
              alreadyInstalledFeatures={alreadyInstalledFeatures}
              isEKSProvider={isEKSCluster(cluster)}
            />
          }
          rightTitle="Run script below"
          right={
            <>
              <ScriptSection
                script={script}
                isLoading={isLoading || isFetching || isActiveFeaturesLoading}
                refetch={refetch}
                error={error}
                overlay={
                  isPreTrial && hasPremiumFeatureSelected ? (
                    <TrialScriptOverlay script={script} />
                  ) : (
                    <AnimatedOverlay
                      visible={isEnteringManually}
                      testId="manual-config-overlay"
                    />
                  )
                }
              />

              {!!securityConstruct && (
                <ManualCredentialsSection
                  securityConstruct={securityConstruct}
                  input={input}
                  apiError={apiError}
                  isSubmitting={isSubmitting}
                  isEnteringManually={isEnteringManually}
                  setIsEnteringManually={setIsEnteringManually}
                />
              )}
            </>
          }
        />

        <DialogFooter
          end={
            isEnteringManually ? (
              <Button
                type="submit"
                loading={isSubmitting}
                disabled={isSubmitting}
                testId="submit-button"
              >
                Submit
              </Button>
            ) : (
              <IRanTheScriptButton
                onClick={() => {
                  setActiveSlide('inProgress');
                }}
              />
            )
          }
        />
      </RffForm>
    </StepContainer>
  );
};
